import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Sparkles, ArrowRight, ArrowLeft, Check, MapPin, MessageCircle,
  Palette, UserPlus, PartyPopper, Plus, X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ImageUpload } from '../components/ImageUpload';
import { ESTADOS } from '../components/StateCitySelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRESET_COLORS = ['#0D1B3D', '#1e3a8a', '#0f766e', '#7c3aed', '#dc2626', '#ea580c', '#059669', '#0891b2'];

const STEPS = [
  { id: 'welcome', label: 'Boas-vindas' },
  { id: 'identity', label: 'Identidade' },
  { id: 'region', label: 'Atuação' },
  { id: 'objetivo', label: 'Objetivo' },
  { id: 'destination', label: 'WhatsApp' },
  { id: 'invites', label: 'Equipe' },
  { id: 'finish', label: 'Pronto' },
];

export const OnboardingWizard = () => {
  const navigate = useNavigate();
  const { user, activeTenant, checkAuth } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [skippedSteps, setSkippedSteps] = useState([]);

  const [campaignName, setCampaignName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#0D1B3D');
  const [logoUrl, setLogoUrl] = useState('');
  const [states, setStates] = useState([]);
  const [cargoObjetivo, setCargoObjetivo] = useState('');
  const [metaSharePct, setMetaSharePct] = useState(20);
  const [destination, setDestination] = useState({ nome: '', tipo: 'grupo', url_whatsapp: '' });
  const [invites, setInvites] = useState([{ email: '', name: '', role: 'coordenador' }]);

  useEffect(() => {
    if (activeTenant?.name) setCampaignName(activeTenant.name);
    if (activeTenant?.primary_color) setPrimaryColor(activeTenant.primary_color);
    if (activeTenant?.logo_url) setLogoUrl(activeTenant.logo_url);
  }, [activeTenant]);

  const currentStep = STEPS[stepIdx];
  const totalSteps = STEPS.length;
  const progress = useMemo(() => Math.round(((stepIdx + 1) / totalSteps) * 100), [stepIdx, totalSteps]);

  const goNext = () => setStepIdx((i) => Math.min(i + 1, totalSteps - 1));
  const goBack = () => setStepIdx((i) => Math.max(i - 1, 0));

  const skipCurrent = () => {
    setSkippedSteps((s) => Array.from(new Set([...s, currentStep.id])));
    goNext();
  };

  const toggleState = (sigla) => {
    setStates((arr) => arr.includes(sigla) ? arr.filter((s) => s !== sigla) : [...arr, sigla]);
  };

  const updateInvite = (idx, key, value) => {
    setInvites((arr) => arr.map((it, i) => i === idx ? { ...it, [key]: value } : it));
  };
  const addInvite = () => setInvites((arr) => arr.length < 5 ? [...arr, { email: '', name: '', role: 'coordenador' }] : arr);
  const removeInvite = (idx) => setInvites((arr) => arr.filter((_, i) => i !== idx));

  const submit = async () => {
    setSaving(true);
    try {
      const validInvites = invites
        .map((i) => ({ ...i, email: i.email.trim().toLowerCase(), name: i.name.trim() }))
        .filter((i) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(i.email));

      const hasDest = destination.nome.trim() && destination.url_whatsapp.trim();
      const payload = {
        campaign_name: campaignName.trim() || undefined,
        primary_color: primaryColor || undefined,
        logo_url: logoUrl || null,
        allowed_states: states,
        cargo_objetivo: cargoObjetivo || null,
        meta_share_pct: typeof metaSharePct === 'number' ? metaSharePct : parseFloat(metaSharePct) || 20,
        first_destination: hasDest ? destination : null,
        invites: validInvites.length ? validInvites : null,
        skipped_steps: skippedSteps,
      };
      await axios.post(`${BACKEND_URL}/api/onboarding/complete`, payload, { withCredentials: true });
      await checkAuth();
      toast.success('Tudo pronto! Bem-vindo ao MeAdd.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao concluir o onboarding.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/40" data-testid="onboarding-wizard">
      <div className="mx-auto max-w-3xl px-6 py-10 sm:py-14">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <img src="/brand/meadd-logo.png" alt="MeAdd" className="h-9 object-contain" />
            <Badge variant="outline" className="text-xs font-manrope">Configuração inicial</Badge>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              try {
                await axios.post(`${BACKEND_URL}/api/onboarding/skip`, {}, { withCredentials: true });
                await checkAuth();
                navigate('/dashboard');
              } catch (err) {
                toast.error('Não foi possível pular. Tente novamente.');
              }
            }}
            className="text-slate-500"
            data-testid="onboarding-skip-all"
          >
            Pular tudo
          </Button>
        </div>

        {/* Progress */}
        <div className="mb-8" data-testid="onboarding-progress">
          <div className="flex justify-between text-xs text-slate-500 mb-2 font-manrope">
            <span>Passo {stepIdx + 1} de {totalSteps}</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <div className="mt-3 hidden sm:flex justify-between gap-1">
            {STEPS.map((s, i) => (
              <div
                key={s.id}
                className={`flex-1 text-center text-[10px] uppercase tracking-wider font-medium font-manrope ${
                  i <= stepIdx ? 'text-emerald-700' : 'text-slate-400'
                }`}
              >
                {s.label}
              </div>
            ))}
          </div>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6 sm:p-10 space-y-6">
            {currentStep.id === 'welcome' && (
              <div className="space-y-5 text-center" data-testid="step-welcome">
                <div className="inline-flex h-14 w-14 rounded-2xl bg-emerald-100 items-center justify-center">
                  <Sparkles className="h-7 w-7 text-emerald-600" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 font-outfit">
                  Bem-vindo, {user?.name?.split(' ')[0] || 'tudo bem'}!
                </h1>
                <p className="text-slate-600 max-w-md mx-auto font-manrope leading-relaxed">
                  Em <strong>menos de 2 minutos</strong> vamos deixar sua plataforma pronta para mobilizar.
                  Você pode pular qualquer passo e ajustar depois nas configurações.
                </p>
                <ul className="text-left max-w-sm mx-auto space-y-2 text-sm text-slate-700 font-manrope">
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Identidade visual da campanha</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Estados onde você vai atuar</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Primeiro grupo de WhatsApp</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Convide sua equipe</li>
                </ul>
              </div>
            )}

            {currentStep.id === 'identity' && (
              <div className="space-y-6" data-testid="step-identity">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-violet-100 flex items-center justify-center">
                    <Palette className="h-5 w-5 text-violet-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 font-outfit">Identidade da campanha</h2>
                    <p className="text-sm text-slate-500 font-manrope">Nome, cor e logo aparecem em todo lugar.</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="campaign_name">Nome da campanha</Label>
                    <Input
                      id="campaign_name"
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      placeholder="Ex: Candidato João da Silva"
                      className="mt-1.5"
                      data-testid="onboarding-campaign-name"
                    />
                  </div>
                  <div>
                    <Label>Cor primária</Label>
                    <div className="flex flex-wrap gap-2 mt-2" data-testid="onboarding-color-grid">
                      {PRESET_COLORS.map((c) => (
                        <button
                          type="button"
                          key={c}
                          onClick={() => setPrimaryColor(c)}
                          className={`h-9 w-9 rounded-lg border-2 transition-all ${
                            primaryColor === c ? 'border-slate-900 scale-110 shadow' : 'border-transparent'
                          }`}
                          style={{ background: c }}
                          aria-label={`cor ${c}`}
                          data-testid={`onboarding-color-${c.replace('#','')}`}
                        />
                      ))}
                      <div className="flex items-center gap-2 ml-1">
                        <input
                          type="color"
                          value={primaryColor}
                          onChange={(e) => setPrimaryColor(e.target.value)}
                          className="h-9 w-12 rounded-lg border border-slate-200 cursor-pointer"
                          data-testid="onboarding-color-custom"
                        />
                        <span className="text-xs text-slate-500 font-mono">{primaryColor}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>Logo da campanha (opcional)</Label>
                    <div className="mt-2">
                      <ImageUpload
                        value={logoUrl}
                        onChange={setLogoUrl}
                        folder={`meadd/tenants/${activeTenant?.id || 'unknown'}/logo`}
                        label=""
                        aspect="aspect-[3/1]"
                        testId="onboarding-logo-upload"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep.id === 'region' && (
              <div className="space-y-6" data-testid="step-region">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 font-outfit">Onde você vai atuar?</h2>
                    <p className="text-sm text-slate-500 font-manrope">Selecione os estados. Usaremos para filtrar lideranças e cidades.</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="onboarding-states-grid">
                  {ESTADOS.map((e) => {
                    const active = states.includes(e.sigla);
                    return (
                      <button
                        type="button"
                        key={e.sigla}
                        onClick={() => toggleState(e.sigla)}
                        data-testid={`onboarding-state-${e.sigla}`}
                        className={`px-3 py-2.5 rounded-lg text-sm font-medium border-2 transition-all font-manrope ${
                          active
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <span className="font-bold">{e.sigla}</span>
                        <div className="text-[10px] text-slate-500 truncate">{e.nome}</div>
                      </button>
                    );
                  })}
                </div>
                {states.length > 0 && (
                  <p className="text-sm text-slate-600 font-manrope">
                    Selecionados: <strong>{states.join(', ')}</strong>
                  </p>
                )}
              </div>
            )}

            {currentStep.id === 'objetivo' && (
              <div className="space-y-6" data-testid="step-objetivo">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <span className="text-xl">🎯</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 font-outfit">Objetivo da campanha</h2>
                    <p className="text-sm text-slate-500 font-manrope">
                      Qual cargo vocês estão disputando? Usamos isso para puxar votos válidos do TSE e sugerir metas por cidade.
                    </p>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700">Cargo objetivo</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                    {[
                      { v: 'prefeito', l: 'Prefeito', y: 2024 },
                      { v: 'vereador', l: 'Vereador', y: 2024 },
                      { v: 'deputado_federal', l: 'Dep. Federal', y: 2022 },
                      { v: 'deputado_estadual', l: 'Dep. Estadual', y: 2022 },
                      { v: 'deputado_distrital', l: 'Dep. Distrital', y: 2022 },
                      { v: 'senador', l: 'Senador', y: 2022 },
                      { v: 'governador', l: 'Governador', y: 2022 },
                      { v: 'presidente', l: 'Presidente', y: 2022 },
                    ].map((c) => {
                      const active = cargoObjetivo === c.v;
                      return (
                        <button
                          type="button"
                          key={c.v}
                          onClick={() => setCargoObjetivo(c.v)}
                          data-testid={`onboarding-cargo-${c.v}`}
                          className={`px-3 py-2.5 rounded-lg text-sm font-medium border-2 transition-all font-manrope text-left ${
                            active
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                          }`}
                        >
                          <span className="font-bold">{c.l}</span>
                          <div className="text-[10px] text-slate-500">eleição {c.y}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700">Share padrão sugerido (%)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    step={0.5}
                    value={metaSharePct}
                    onChange={(e) => setMetaSharePct(parseFloat(e.target.value) || 0)}
                    className="mt-1.5 max-w-xs"
                    data-testid="onboarding-share-pct"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Vereador 5-8% · Prefeito 35-50% · Dep. Federal 1-3% · Senador/Governador 25-40% · Presidente 30%+
                  </p>
                </div>
              </div>
            )}

            {currentStep.id === 'destination' && (
              <div className="space-y-6" data-testid="step-destination">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 font-outfit">Primeiro grupo WhatsApp</h2>
                    <p className="text-sm text-slate-500 font-manrope">Para onde seus apoiadores serão direcionados ao escanear o QR Code.</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="dest_nome">Nome do grupo/canal</Label>
                    <Input
                      id="dest_nome"
                      value={destination.nome}
                      onChange={(e) => setDestination({ ...destination, nome: e.target.value })}
                      placeholder="Ex: Grupo Oficial — Apoiadores"
                      className="mt-1.5"
                      data-testid="onboarding-dest-nome"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dest_tipo">Tipo</Label>
                    <select
                      id="dest_tipo"
                      value={destination.tipo}
                      onChange={(e) => setDestination({ ...destination, tipo: e.target.value })}
                      className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                      data-testid="onboarding-dest-tipo"
                    >
                      <option value="grupo">Grupo de WhatsApp</option>
                      <option value="canal">Canal de WhatsApp</option>
                      <option value="comunidade">Comunidade de WhatsApp</option>
                      <option value="atendimento">Link de Atendimento</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="dest_url">Link do WhatsApp</Label>
                    <Input
                      id="dest_url"
                      value={destination.url_whatsapp}
                      onChange={(e) => setDestination({ ...destination, url_whatsapp: e.target.value })}
                      placeholder="https://chat.whatsapp.com/..."
                      className="mt-1.5"
                      data-testid="onboarding-dest-url"
                    />
                    <p className="text-xs text-slate-500 mt-1 font-manrope">
                      Cole o link de convite do seu grupo. Vamos defini-lo como destino padrão.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {currentStep.id === 'invites' && (
              <div className="space-y-6" data-testid="step-invites">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <UserPlus className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 font-outfit">Convide sua equipe</h2>
                    <p className="text-sm text-slate-500 font-manrope">Coordenadores recebem um e-mail com convite. Até 5 convites agora.</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {invites.map((inv, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_160px_40px] gap-2 items-end" data-testid={`onboarding-invite-${idx}`}>
                      <div>
                        {idx === 0 && <Label className="text-xs">E-mail</Label>}
                        <Input
                          value={inv.email}
                          onChange={(e) => updateInvite(idx, 'email', e.target.value)}
                          placeholder="coordenador@email.com"
                          type="email"
                          data-testid={`onboarding-invite-email-${idx}`}
                        />
                      </div>
                      <div>
                        {idx === 0 && <Label className="text-xs">Nome (opcional)</Label>}
                        <Input
                          value={inv.name}
                          onChange={(e) => updateInvite(idx, 'name', e.target.value)}
                          placeholder="Nome completo"
                          data-testid={`onboarding-invite-name-${idx}`}
                        />
                      </div>
                      <div>
                        {idx === 0 && <Label className="text-xs">Papel</Label>}
                        <select
                          value={inv.role}
                          onChange={(e) => updateInvite(idx, 'role', e.target.value)}
                          className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                          data-testid={`onboarding-invite-role-${idx}`}
                        >
                          <option value="coordenador">Coordenador</option>
                          <option value="lideranca">Liderança</option>
                          <option value="juridico">Jurídico</option>
                        </select>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeInvite(idx)}
                        disabled={invites.length === 1}
                        className="text-slate-400 hover:text-rose-500"
                        data-testid={`onboarding-invite-remove-${idx}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addInvite}
                    disabled={invites.length >= 5}
                    data-testid="onboarding-invite-add"
                    className="font-manrope"
                  >
                    <Plus className="h-4 w-4 mr-1.5" /> Adicionar outro
                  </Button>
                </div>
              </div>
            )}

            {currentStep.id === 'finish' && (
              <div className="space-y-6 text-center" data-testid="step-finish">
                <div className="inline-flex h-16 w-16 rounded-full bg-emerald-100 items-center justify-center">
                  <PartyPopper className="h-8 w-8 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-slate-900 font-outfit">Tudo pronto!</h2>
                  <p className="text-slate-600 mt-2 font-manrope">
                    Revise as informações e finalize. Você pode editar tudo depois em Configurações.
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-5 text-left space-y-3 text-sm" data-testid="onboarding-summary">
                  <SummaryRow label="Campanha" value={campaignName || '—'} />
                  <SummaryRow label="Cor primária" value={
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block h-4 w-4 rounded" style={{ background: primaryColor }} />
                      <span className="font-mono">{primaryColor}</span>
                    </span>
                  } />
                  <SummaryRow label="Logo" value={logoUrl ? 'Carregada' : 'Sem logo'} />
                  <SummaryRow label="Estados" value={states.length ? states.join(', ') : '—'} />
                  <SummaryRow label="Primeiro destino" value={destination.nome || '—'} />
                  <SummaryRow label="Convites" value={
                    invites.filter((i) => i.email.trim()).length
                      ? `${invites.filter((i) => i.email.trim()).length} pessoas`
                      : '—'
                  } />
                </div>
              </div>
            )}

            {/* Footer / nav */}
            <div className="flex items-center justify-between pt-6 border-t border-slate-100">
              <Button
                variant="ghost"
                onClick={goBack}
                disabled={stepIdx === 0}
                data-testid="onboarding-back"
                className="text-slate-500 font-manrope"
              >
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Voltar
              </Button>

              <div className="flex items-center gap-2">
                {currentStep.id !== 'welcome' && currentStep.id !== 'finish' && (
                  <Button
                    variant="ghost"
                    onClick={skipCurrent}
                    data-testid="onboarding-skip-step"
                    className="text-slate-500 font-manrope"
                  >
                    Pular este passo
                  </Button>
                )}
                {currentStep.id === 'finish' ? (
                  <Button
                    onClick={submit}
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold font-manrope min-w-[180px]"
                    data-testid="onboarding-finish"
                  >
                    {saving ? 'Finalizando...' : (<>Concluir e ir ao painel <ArrowRight className="h-4 w-4 ml-1.5" /></>)}
                  </Button>
                ) : (
                  <Button
                    onClick={goNext}
                    className="bg-slate-900 hover:bg-slate-800 text-white font-semibold font-manrope"
                    data-testid="onboarding-next"
                  >
                    Continuar <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-slate-500 font-manrope">{label}</span>
    <span className="font-semibold text-slate-900 text-right truncate max-w-[60%]">{value}</span>
  </div>
);

export default OnboardingWizard;
